// Moved from `src/hooks/mutation/useMutateUpdateTeam.ts` (now the app delegate)
// with the OT4 mutation slice (ARCFE-1033).
//
// Severed per ARCFE-921: the destructive toast and the Rematch
// `analyticsScribe.trackTeamUpdated` dispatch. `UPDATE_TEAM` was module-private
// app-side and stays module-private here — it is a mutationKey, not a cache
// identity, so it is not a `/contracts` concern.
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { GET_TEAM_DETAILS } from '../../contracts';
import { updateTeam } from '../api/teams';
import { isTransportError, type WorkspaceHttpError } from '../config';

const UPDATE_TEAM = 'UpdateTeam';

export interface UpdateTeamVariables {
  teamName: string;
}

export interface UseMutateUpdateTeamOptions {
  /** App-reaction port: fired where the Rematch `trackTeamUpdated` fired. */
  onTeamUpdated?: (result: { teamId: string; teamName: string }) => void;
  /** App-reaction port: fired where the legacy destructive toast fired. */
  onUpdateError?: (error: WorkspaceHttpError) => void;
}

/** PATCH /teams/{team_id} */
export function useMutateUpdateTeam(
  teamId: string,
  options?: UseMutateUpdateTeamOptions
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [UPDATE_TEAM],
    mutationFn: async (variables: UpdateTeamVariables) => {
      return await updateTeam(teamId, variables.teamName);
    },
    onSuccess: (_, variables) => {
      options?.onTeamUpdated?.({ teamId, teamName: variables.teamName });
    },
    onError: (error) => {
      if (isTransportError(error)) {
        options?.onUpdateError?.(error);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [GET_TEAM_DETAILS, { teamId: teamId }],
      });
    },
  });
}
